/* src/modules/performance/PerformanceSetup.js */
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, addDoc, deleteDoc, doc, orderBy, getDocs, updateDoc, where, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
    PlusIcon, 
    TrashIcon, 
    EyeIcon, 
    EditIcon, 
    XCircleIcon,
    UserGroupIcon,
    CheckCircleIcon,
    DocumentAddIcon,
    DownloadIcon,
    LockClosedIcon
} from '../../components/icons/Icons';
import jsPDF from 'jspdf'; // Added
import autoTable from 'jspdf-autotable'; // Added

// --- CONSTANTS ---
export const RATING_OPTIONS = [
    "Greatly Exceeds Expectations",
    "Exceeds Expectations",
    "Meets Expectations",
    "Needs Improvement",
    "Needs Significant Improvement"
];

// --- HELPER: ISO Date Formatter ---
const formatDateISO = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return date.toISOString().split('T')[0];
    } catch (e) {
        return val;
    }
};

// --- NEW HELPER: Date & Time Formatter ---
const formatDateTime = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return val;
    }
};

export default function PerformanceSetup({ userProfile, appId }) {
    const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'report'
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Fetch existing templates
    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/performance_templates`), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, (snapshot) => {
            setTemplates(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId]);

    // SAFE DELETE
    const handleDelete = async (template) => {
        if(!window.confirm("Are you sure you want to delete this template?")) return;

        const reviewsRef = collection(db, `artifacts/${appId}/performance_reviews`);
        const q = query(reviewsRef, where('cycle', '==', template.name));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
            alert(`Cannot delete "${template.name}". There are ${snapshot.size} submitted reviews associated with this cycle. Deleting it would cause data loss.`);
            return;
        }

        try {
            await deleteDoc(doc(db, `artifacts/${appId}/performance_templates`, template.id));
        } catch (error) {
            console.error(error);
            alert("Error deleting template.");
        }
    };

    // --- NEW: CLOSE CYCLE ---
    const handleCloseCycle = async (template) => {
        if(!window.confirm(`Are you sure you want to close "${template.name}"? Employees and managers will no longer be able to edit their reviews.`)) return;
        
        try {
            // 1. Mark template as closed
            await updateDoc(doc(db, `artifacts/${appId}/performance_templates`, template.id), { status: 'Closed' });
            
            // 2. Batch update all active reviews in this cycle to 'Review Closed'
            const reviewsRef = collection(db, `artifacts/${appId}/performance_reviews`);
            const q = query(reviewsRef, where('templateId', '==', template.id));
            const snapshot = await getDocs(q);
            
            const batch = writeBatch(db);
            snapshot.docs.forEach(d => {
                const data = d.data();
                // We leave already 'Completed' ones alone, everything else gets locked
                if (data.status !== 'Completed') {
                    batch.update(d.ref, { status: 'Review Closed' });
                }
            });
            await batch.commit();
            alert("Cycle closed successfully.");
        } catch (error) {
            console.error(error);
            alert("Error closing cycle.");
        }
    };

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setView('edit');
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            {view === 'list' && (
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-[#213368]">Performance Templates</h2>
                        <button 
                            onClick={() => { setSelectedTemplate(null); setView('create'); }} 
                            className="flex items-center px-4 py-2 bg-[#20BEC6] text-white rounded-lg hover:opacity-90"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" /> Create New Cycle
                        </button>
                    </div>

                    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {templates.map(t => (
                                    <tr key={t.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-[#213368]">{t.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">{t.deadline}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full ${t.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {t.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap flex space-x-3">
                                            <button onClick={() => { setSelectedTemplate(t); setView('report'); }} className="text-blue-600 hover:text-blue-800 flex items-center text-sm" title="View Reports">
                                                <EyeIcon className="h-5 w-5" />
                                            </button>
                                            {t.status === 'Active' && (
                                                <>
                                                    <button onClick={() => handleEdit(t)} className="text-gray-600 hover:text-gray-800 flex items-center text-sm" title="Edit Template">
                                                        <EditIcon className="h-5 w-5" />
                                                    </button>
                                                    <button onClick={() => handleCloseCycle(t)} className="text-orange-500 hover:text-orange-700 flex items-center text-sm" title="Close Cycle">
                                                        <LockClosedIcon className="h-5 w-5" />
                                                    </button>
                                                </>
                                            )}
                                            <button onClick={() => handleDelete(t)} className="text-red-500 hover:text-red-700" title="Delete">
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {templates.length === 0 && (
                                    <tr><td colSpan="4" className="text-center py-8 text-gray-500">No templates found. Create one to get started.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {(view === 'create' || view === 'edit') && (
                <TemplateBuilder 
                    appId={appId} 
                    existingTemplates={templates}
                    initialData={selectedTemplate} 
                    isEditMode={view === 'edit'}
                    onCancel={() => { setView('list'); setSelectedTemplate(null); }} 
                    onSuccess={() => { setView('list'); setSelectedTemplate(null); }} 
                />
            )}

            {view === 'report' && selectedTemplate && (
                <CycleReportView 
                    appId={appId} 
                    template={selectedTemplate} 
                    onBack={() => { setView('list'); setSelectedTemplate(null); }} 
                />
            )}
        </div>
    );
}

// --- SUB-COMPONENT: Template Builder ---
function TemplateBuilder({ appId, existingTemplates, initialData, isEditMode, onCancel, onSuccess }) {
    const [name, setName] = useState(initialData ? initialData.name : '');
    const [deadline, setDeadline] = useState(initialData ? initialData.deadline : '');
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    
    const defaultSections = [
        { id: 1, type: 'achievements_table', title: 'Key Accomplishments', description: 'Outline 3-5 key accomplishments and map them to company values.' },
        { id: 2, type: 'standard_table', title: 'Challenges / Areas of Development', description: 'Identify key challenges faced during this period.' },
        { id: 3, type: 'standard_table', title: 'Job Skills & Competencies', description: 'Highlight key strengths that align with your role.' },
        { id: 4, type: 'standard_table', title: '6-Month Development Goals', description: 'Outline 3 key development goals for the next 6 months.' },
        { id: 5, type: 'training_table', title: 'Professional Development', description: 'Record your training completion status.' },
        { id: 6, type: 'rating', title: 'Overall Performance', description: 'Overall rating and business impact.' }
    ];

    const [sections, setSections] = useState(initialData ? initialData.sections : defaultSections);
    const [isSaving, setIsSaving] = useState(false);

    const handleTemplateSelect = (e) => {
        const templateId = e.target.value;
        if (!templateId) return;
        
        const templateToCopy = existingTemplates.find(t => t.id === templateId);
        if (templateToCopy) {
            if (sections.length > 0 && window.confirm("This will overwrite your current form structure. Continue?")) {
                setSections(templateToCopy.sections);
            }
        }
    };

    const addSection = () => {
        setSections([...sections, { id: Date.now(), type: 'text', title: 'New Section', description: '' }]);
    };

    const removeSection = (id) => {
        setSections(sections.filter(s => s.id !== id));
    };

    const updateSection = (id, field, value) => {
        setSections(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
    };

    const handleSave = async () => {
        if (!name || !deadline) return alert("Please enter a name and deadline.");
        setIsSaving(true);
        try {
            const templateData = {
                name,
                deadline,
                status: 'Active',
                sections,
                updatedAt: new Date()
            };

            if (isEditMode && initialData) {
                await updateDoc(doc(db, `artifacts/${appId}/performance_templates`, initialData.id), templateData);
            } else {
                templateData.createdAt = new Date();
                await addDoc(collection(db, `artifacts/${appId}/performance_templates`), templateData);
            }
            onSuccess();
        } catch (error) {
            console.error(error);
            alert("Failed to save template.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[#213368]">
                    {isEditMode ? 'Edit Review Cycle' : 'Create New Review Cycle'}
                </h3>
                <button onClick={() => setIsPreviewOpen(true)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center">
                    <EyeIcon className="h-5 w-5 mr-2" /> Preview Form
                </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Name</label>
                    <input type="text" className="w-full p-2 border rounded" placeholder="e.g., Annual Review 2025" value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Submission Deadline</label>
                    <input type="date" className="w-full p-2 border rounded" value={deadline} onChange={e => setDeadline(e.target.value)} />
                </div>
            </div>

            {!isEditMode && existingTemplates.length > 0 && (
                <div className="mb-8 p-4 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between">
                    <div>
                        <h4 className="font-semibold text-[#213368] text-sm">Copy structure from previous cycle?</h4>
                        <p className="text-xs text-gray-500">Select a template to pre-fill the sections below.</p>
                    </div>
                    <div className="w-1/2">
                        <select onChange={handleTemplateSelect} className="w-full p-2 border rounded bg-white text-sm">
                            <option value="">-- Select a template --</option>
                            {existingTemplates.map(t => (
                                <option key={t.id} value={t.id}>{t.name} (Deadline: {t.deadline})</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="space-y-4 mb-8">
                <h4 className="font-semibold text-gray-700 border-b pb-2">Form Structure</h4>
                {sections.map((section) => (
                    <div key={section.id} className="p-4 bg-gray-50 border rounded-lg relative group transition hover:shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label className="text-xs text-gray-500 uppercase">Section Title</label>
                                <input type="text" className="w-full p-2 border rounded mb-2" value={section.title} onChange={(e) => updateSection(section.id, 'title', e.target.value)} />
                                <label className="text-xs text-gray-500 uppercase">Instructions / Description</label>
                                <textarea className="w-full p-2 border rounded text-sm" rows="2" value={section.description} onChange={(e) => updateSection(section.id, 'description', e.target.value)} />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Input Type</label>
                                <select className="w-full p-2 border rounded" value={section.type} onChange={(e) => updateSection(section.id, 'type', e.target.value)}>
                                    <option value="text">Open Text Box</option>
                                    <option value="achievements_table">Table: Achievements (Values Dropdown)</option>
                                    <option value="standard_table">Table: Standard (Item + Comment)</option>
                                    <option value="training_table">Table: Professional Development</option>
                                    <option value="rating">Overall Rating Scale</option>
                                </select>
                            </div>
                        </div>
                        <button onClick={() => removeSection(section.id)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 p-1">
                            <TrashIcon className="h-5 w-5" />
                        </button>
                    </div>
                ))}
                
                <button onClick={addSection} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-[#20BEC6] hover:text-[#20BEC6] font-medium transition">
                    + Add Form Section
                </button>
            </div>

            <div className="flex justify-end space-x-4">
                <button onClick={onCancel} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-[#213368] text-white rounded-lg hover:opacity-90">
                    {isSaving ? 'Saving...' : (isEditMode ? 'Update Template' : 'Save & Publish Template')}
                </button>
            </div>

            {isPreviewOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col">
                        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                            <h2 className="text-xl font-bold text-[#213368]">Preview: {name || "Untitled Cycle"}</h2>
                            <button onClick={() => setIsPreviewOpen(false)}><XCircleIcon className="h-6 w-6 text-gray-400" /></button>
                        </div>
                        <div className="p-8 space-y-8 bg-gray-50">
                            {sections.map((sec, idx) => (
                                <div key={idx} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <h4 className="text-lg font-bold text-[#213368] mb-2">{idx + 1}. {sec.title}</h4>
                                    <p className="text-sm text-gray-600 mb-4 whitespace-pre-wrap">{sec.description}</p>
                                    
                                    {sec.type === 'text' && <textarea disabled placeholder="Employee answer..." className="w-full p-2 border rounded bg-gray-50" rows="3"></textarea>}
                                    
                                    {(sec.type === 'achievements_table' || sec.type === 'standard_table' || sec.type === 'training_table') && (
                                        <div className="border rounded overflow-hidden">
                                            <div className="p-4 text-center text-gray-400 italic text-sm">Table will be rendered here...</div>
                                        </div>
                                    )}

                                    {sec.type === 'rating' && (
                                        <div className="p-6 bg-blue-50 rounded border border-blue-100">
                                            <label className="block text-sm font-bold text-[#213368] mb-2">Overall Performance Rating</label>
                                            <select className="w-full p-2 border border-gray-300 rounded-md bg-white">
                                                <option>-- Select Rating --</option>
                                                {RATING_OPTIONS.map((opt, i) => (
                                                    <option key={i}>{opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t bg-gray-100 text-right">
                            <button onClick={() => setIsPreviewOpen(false)} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Close Preview</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- SUB-COMPONENT: Report View (Updated with Stats) ---
function CycleReportView({ appId, template, onBack }) {
    const [reviews, setReviews] = useState([]);
    const [totalActiveEmployees, setTotalActiveEmployees] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedReviewForView, setSelectedReviewForView] = useState(null);
    
    // Search & Sort State
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // 1. Fetch Users (Count) and Reviews
 /* Inside CycleReportView Component */
useEffect(() => {
    const fetchData = async () => {
        try {
            // 1. Fetch all users to create a Name Lookup Map
            const usersSnap = await getDocs(collection(db, `artifacts/${appId}/users`));
            
            // Create a map where key is UID and value is User Name
            const userNameMap = {};
            const approvedUsers = [];

            usersSnap.docs.forEach(doc => {
                const data = doc.data();
                const uid = doc.id;
                userNameMap[uid] = data.name || "Unknown User";
                
                if (data.status === 'Approved') {
                    approvedUsers.push({ uid, ...data });
                }
            });
            
            setTotalActiveEmployees(approvedUsers.length);

            // 2. Fetch Existing Reviews for this cycle
            const q = query(
                collection(db, `artifacts/${appId}/performance_reviews`), 
                where('cycle', '==', template.name)
            );
            const reviewsSnap = await getDocs(q);
            const existingReviews = reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // 3. Merge Logic with Manager Name Lookup
            const finalReportRows = approvedUsers.map(user => {
                const review = existingReviews.find(r => r.userId === user.uid);
                
                if (review) {
                    return {
                        ...review,
                        department: user.department || 'Unassigned',
                        // Fallback to the Name Map if managerName is missing in the review doc
                        managerName: review.managerName || userNameMap[user.managerId] || 'Not Assigned'
                    };
                } else {
                    return {
                        id: `ns-${user.uid}`,
                        userId: user.uid,
                        userName: user.name,
                        department: user.department || 'Unassigned',
                        status: 'Not Started',
                        // Dynamic lookup: Convert managerId to managerName
                        managerName: userNameMap[user.managerId] || 'Not Assigned',
                        updatedAt: null,
                        finalRating: '-'
                    };
                }
            });

            setReviews(finalReportRows);
            setLoading(false);
        } catch (err) {
            console.error("Error mapping managers:", err);
            setLoading(false);
        }
    };
    fetchData();
}, [appId, template.name]);

    // 2. Calculate Stats
 const stats = useMemo(() => {
    // 1. Filter out the placeholders to get actual started reviews
    const actualStartedReviews = reviews.filter(r => r.status !== 'Not Started');
    
    const started = actualStartedReviews.length;
    const notStarted = reviews.filter(r => r.status === 'Not Started').length;
    
    let counts = {
        draft: 0,
        submitted: 0, 
        completed: 0
    };

    // 2. Count statuses based on actual started reviews only
    actualStartedReviews.forEach(r => {
        if (r.status === 'Draft') counts.draft++;
        else if (r.status === 'Completed') counts.completed++;
        else counts.submitted++;  // Manager-Review or Submitted
    });

    const participationRate = totalActiveEmployees > 0 
        ? Math.round((started / totalActiveEmployees) * 100) 
        : 0;

    return { started, notStarted, participationRate, counts };
}, [reviews, totalActiveEmployees]);
    // 3. Filter & Sort Logic
    const processedReviews = useMemo(() => {
        let filtered = [...reviews];
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(r => 
                r.userName.toLowerCase().includes(term) ||
                r.department.toLowerCase().includes(term) ||
                r.managerName?.toLowerCase().includes(term)
            );
        }
        filtered.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'updatedAt') {
                aVal = aVal?.toMillis ? aVal.toMillis() : 0;
                bVal = bVal?.toMillis ? bVal.toMillis() : 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [reviews, searchTerm, sortConfig]);

    const paginatedReviews = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return processedReviews.slice(start, start + itemsPerPage);
    }, [processedReviews, currentPage, itemsPerPage]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

     // --- NEW: Export to CSV Function ---
    const exportToCSV = () => {
        const headers = ["Employee", "Department", "Status", "Manager", "Final Rating", "Last Action"];
        
        // Use processedReviews so the export respects the current search/sort filters
        const rows = processedReviews.map(r => [
            `"${r.userName || ''}"`,
            `"${r.department || ''}"`,
            `"${r.status || ''}"`,
            `"${r.managerName || ''}"`,
            `"${r.finalRating || '-'}"`,
            `"${formatDateTime(r.updatedAt)}"`
        ]);

        const csvContent = "data:text/csv;charset=utf-8," 
            + headers.join(",") + "\n" 
            + rows.map(e => e.join(",")).join("\n");
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${template.name.replace(/[^a-z0-9]/gi, '_')}_Report.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    // -----------------------------------
    
     if (loading) return <LoadingSpinner />;

    // NEW: Render Admin Viewer if selected
    if (selectedReviewForView) {
        return (
            <AdminReviewViewer 
                review={selectedReviewForView} 
                template={template} 
                onBack={() => setSelectedReviewForView(null)} 
            />
        );
    }

    return (
        <div>
            <button onClick={onBack} className="mb-4 text-sm text-gray-500 hover:text-[#213368] flex items-center">
                ← Back to Templates
            </button>
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-[#213368]">{template.name} Report</h2>
                        <p className="text-sm text-gray-500">Deadline: {template.deadline}</p>
                    </div>
                    {/* --- NEW: Export Button --- */}
                    <button 
                        onClick={exportToCSV} 
                        className="flex items-center px-4 py-2 bg-[#213368] text-white rounded-lg hover:opacity-90 text-sm font-medium shadow-sm transition"
                    >
                        <DownloadIcon className="h-4 w-4 mr-2" /> Export CSV
                    </button>
                </div>

                {/* --- STATS DASHBOARD --- */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    {/* Card 1: Participation */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-500 uppercase font-bold">Participation</p>
                        <div className="mt-2 flex items-end space-x-2">
                            <span className="text-2xl font-bold text-[#213368]">{stats.started}</span>
                            <span className="text-sm text-gray-400 mb-1">/ {totalActiveEmployees} Active Users</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                            <div className="bg-[#20BEC6] h-1.5 rounded-full" style={{ width: `${stats.participationRate}%` }}></div>
                        </div>
                    </div>

                    {/* Card 2: Not Started */}
                    <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                        <p className="text-xs text-red-600 uppercase font-bold">Not Started</p>
                        <div className="mt-2 text-2xl font-bold text-red-800">{stats.notStarted}</div>
                        <p className="text-xs text-red-400 mt-1">Users yet to create draft</p>
                    </div>

                    {/* Card 3: In Progress (Draft + Review) */}
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                        <div className="flex justify-between">
                            <div>
                                <p className="text-xs text-yellow-700 uppercase font-bold">In Progress</p>
                                <div className="mt-2 text-2xl font-bold text-yellow-800">{stats.counts.draft + stats.counts.submitted}</div>
                            </div>
                            <div className="text-right text-xs text-yellow-600 space-y-1">
                                <div>Drafts: <b>{stats.counts.draft}</b></div>
                                <div>With Mgr: <b>{stats.counts.submitted}</b></div>
                            </div>
                        </div>
                    </div>

                    {/* Card 4: Completed */}
                    <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                        <p className="text-xs text-green-700 uppercase font-bold">Completed</p>
                        <div className="mt-2 text-2xl font-bold text-green-800">{stats.counts.completed}</div>
                        <p className="text-xs text-green-600 mt-1">Locked by HR</p>
                    </div>
                </div>
                {/* --- END STATS --- */}

                <div className="mb-4">
                    <input 
                        type="text" 
                        placeholder="Search by employee, department, or manager..." 
                        className="w-full p-2 border rounded-lg shadow-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('userName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-[#20BEC6]">Employee</th>
                                <th onClick={() => requestSort('department')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-[#20BEC6]">Department</th>
                                <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-[#20BEC6]">Status</th>
                                <th onClick={() => requestSort('managerName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-[#20BEC6]">Manager</th>
                                <th onClick={() => requestSort('finalRating')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-[#20BEC6]">Final Rating</th>
                                <th onClick={() => requestSort('updatedAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-[#20BEC6]">Last Action</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">View</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedReviews.map(r => (
                                <tr key={r.id} className= "hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-[#213368]">{r.userName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{r.department}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            r.status === 'Not Started' ? 'bg-red-100 text-red-700 border border-red-200' :
                                            r.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                            r.status === 'Review Closed' ? 'bg-gray-200 text-gray-800' :
                                            r.status === 'Submitted' || r.status === 'Manager-Review' ? 'bg-blue-100 text-blue-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{r.managerName || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-700">
                                        {r.finalRating || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                        {formatDateTime(r.updatedAt)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button 
                onClick={() => setSelectedReviewForView(r)}
                disabled={r.status === 'Not Started'} // Disable if no data exists yet
                className={`${r.status === 'Not Started' ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                title={r.status === 'Not Started' ? "No review data" : "View Review Details"}
            >
                                            <EyeIcon className="h-5 w-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {paginatedReviews.length === 0 && (
                                <tr><td colSpan="7" className="text-center py-8 text-gray-500">No reviews match your search.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {processedReviews.length > itemsPerPage && (
                    <div className="flex justify-between items-center mt-4">
                        <div className="text-sm text-gray-600">
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, processedReviews.length)} of {processedReviews.length}
                        </div>
                        <div className="flex space-x-2">
                            <button onClick={() => setCurrentPage(c => Math.max(c - 1, 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50">Prev</button>
                            <button onClick={() => setCurrentPage(c => c + 1)} disabled={currentPage * itemsPerPage >= processedReviews.length} className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- NEW SUB-COMPONENT: Admin Read-Only Viewer (Updated with PDF Download) ---
function AdminReviewViewer({ review, template, onBack }) {
    const managerComments = review.managerComments || {};

    // --- UPDATED PDF FUNCTION FOR ADMIN/HR VIEW ---
    const handleDownloadPDF = () => {
	if (review.status === 'Not Started') {
            alert("Cannot generate a PDF for a review that has not been started.");
            return;
        }
        const doc = new jsPDF();
        
        const logo = new Image();
        logo.src = '/Brand_logo.png';
        
        logo.onload = () => {
            const imgProps = doc.getImageProperties(logo);
            const pdfLogoWidth = 40; 
            const pdfLogoHeight = (imgProps.height * pdfLogoWidth) / imgProps.width;

            const darkBlue = [33, 51, 104];
            const teal = [32, 190, 198];

            doc.addImage(logo, 'PNG', 14, 10, pdfLogoWidth, pdfLogoHeight);
            
            doc.setFontSize(10);
            doc.setTextColor(200, 0, 0);
            doc.text("CONFIDENTIAL", 195, 15, { align: "right" });
            
            doc.setTextColor(...darkBlue);
            doc.setFontSize(16);
            doc.text("Performance Review", 14, pdfLogoHeight + 18);
            
            // --- METADATA SECTION (UPDATED) ---
            doc.setTextColor(0);
            doc.setFontSize(10);
            const startY = pdfLogoHeight + 25;
            
            doc.text(`Employee: ${review.userName}`, 14, startY);
            doc.text(`Manager: ${review.managerName || 'Pending'}`, 14, startY + 5);
            doc.text(`Cycle: ${review.cycle}`, 14, startY + 10);
            
            // [NEW] Added Status Line
            doc.text(`Status: ${review.status}`, 14, startY + 15);
            
            // Shifted Date down
            doc.text(`Date: ${formatDateISO(new Date())}`, 14, startY + 20);
            
            // Shifted Divider Line down
            doc.setDrawColor(...teal);
            doc.setLineWidth(0.5);
            doc.line(14, startY + 25, 196, startY + 25);

            // Shifted Content Start position down
            let finalY = startY + 33;
            // ----------------------------------

            template.sections.forEach((section, index) => {
                if (finalY > 250) { doc.addPage(); finalY = 20; }

                doc.setTextColor(...darkBlue);
                doc.setFontSize(12);
                doc.setFont("helvetica", "bold");
                doc.text(`${index + 1}. ${section.title}`, 14, finalY);
                finalY += 8;

                doc.setTextColor(0);
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");

                const employeeResponse = review.employeeResponses?.[section.id] || [];
                const mgrComment = managerComments[section.id] || "No comments.";

                if (section.type === 'text') {
                    const text = typeof employeeResponse === 'string' ? employeeResponse : "No response.";
                    const splitText = doc.splitTextToSize(text, 180);
                    doc.text(splitText, 14, finalY);
                    finalY += (splitText.length * 5) + 5;
                } 
                else if (section.type === 'achievements_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.text, row.value]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Accomplishment', 'Value']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }
                else if (section.type === 'standard_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.text]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Description']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }
                else if (section.type === 'training_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.category, row.name, row.status]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Category', 'Activity', 'Status']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }

                if (finalY > 250) { doc.addPage(); finalY = 20; }
                
                doc.setFont("helvetica", "bold");
                doc.setTextColor(100);
                doc.text("Manager Feedback:", 14, finalY);
                finalY += 5;
                
                doc.setFont("helvetica", "normal");
                doc.setTextColor(0);
                
                if (section.type === 'rating') {
                    doc.text(`Final Rating: ${review.finalRating}`, 14, finalY);
                    finalY += 6;
                }

                const splitComment = doc.splitTextToSize(mgrComment, 180);
                doc.text(splitComment, 14, finalY);
                finalY += (splitComment.length * 5) + 15;
            });

            const safeName = review.userName.replace(/[^a-z0-9]/gi, '_');
            const safeCycle = review.cycle.replace(/[^a-z0-9]/gi, '_');
            doc.save(`${safeCycle}_${safeName}.pdf`);
        };
    };

    return (
        <div className="space-y-6">
            {/* Header: Back + Download */}
            <div className="flex justify-between items-center">
                <button onClick={onBack} className="text-sm text-gray-500 hover:text-[#213368] flex items-center">
                    ← Back to Report
                </button>
                <button onClick={handleDownloadPDF} className="flex items-center text-sm text-[#20BEC6] hover:text-[#1a9ca3] font-medium">
                    <DownloadIcon className="h-5 w-5 mr-1" /> Download PDF
                </button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex justify-between items-start">
                <div>
                    <h2 className="text-xl font-bold text-[#213368]">{review.userName}</h2>
                    <p className="text-sm text-gray-500">Status: {review.status}</p>
                </div>
                <div className="text-right">
                    <p className="text-xs text-gray-400 uppercase">Final Rating</p>
                    <p className="text-xl font-bold text-[#20BEC6]">{review.finalRating || '-'}</p>
                </div>
            </div>

            <div className="space-y-8">
                {template.sections.map((section, index) => {
                    const employeeData = review.employeeResponses ? review.employeeResponses[section.id] : null;
                    const comment = managerComments[section.id] || '';

                    return (
                        <div key={section.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <h3 className="text-lg font-bold text-[#213368] mb-4 border-b pb-2">{index + 1}. {section.title}</h3>
                            
                            {/* Employee Data */}
                            <div className="mb-6">
                                {section.type !== 'rating' && (
                                    <>
                                        <p className="text-xs font-bold text-gray-400 uppercase mb-2">Employee Response</p>
                                        {section.type === 'text' && <div className="bg-gray-50 p-4 rounded text-gray-800 text-sm whitespace-pre-wrap">{employeeData || "No response."}</div>}
                                        {(section.type === 'achievements_table' || section.type === 'standard_table' || section.type === 'training_table') && (
                                            <ReadOnlyTable data={employeeData || []} type={section.type} />
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Manager Data */}
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Manager Feedback</label>
                                {/* NEW: For Rating Sections, explicitly show the Rating Value if present */}
                                {section.type === 'rating' && review.finalRating && (
                                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                        <span className="text-xs font-bold text-blue-800 uppercase mr-2">Rating Assigned:</span>
                                        <span className="font-bold text-[#213368]">{review.finalRating}</span>
                                    </div>
                                )}
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment || "No feedback."}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// --- HELPER: Read Only Table ---
function ReadOnlyTable({ data, type }) {
    if (!data || data.length === 0) return <p className="text-sm text-gray-400 italic">No data entered.</p>;
    return (
        <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                    <tr>
                        {type === 'achievements_table' && <><th className="px-3 py-2 text-left font-medium text-gray-500">Accomplishment</th><th className="px-3 py-2 text-left font-medium text-gray-500">Value</th></>}
                        {type === 'standard_table' && <th className="px-3 py-2 text-left font-medium text-gray-500">Description</th>}
                        {type === 'training_table' && <><th className="px-3 py-2 text-left font-medium text-gray-500">Category</th><th className="px-3 py-2 text-left font-medium text-gray-500">Activity</th><th className="px-3 py-2 text-left font-medium text-gray-500">Status</th></>}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, i) => (
                        <tr key={i}>
                            {type === 'achievements_table' && <><td className="px-3 py-2 text-gray-800 whitespace-pre-wrap">{row.text}</td><td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.value}</td></>}
                            {type === 'standard_table' && <td className="px-3 py-2 text-gray-800 whitespace-pre-wrap">{row.text}</td>}
                            {type === 'training_table' && <><td className="px-3 py-2 text-gray-600">{row.category}</td><td className="px-3 py-2 text-gray-800">{row.name}</td><td className="px-3 py-2 text-gray-600">{row.status}</td></>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}